import React from 'react';
import {InputText} from 'primereact/inputtext';
import {Dropdown, DropdownChangeParams} from 'primereact/dropdown';
import Editor, {loader} from "@monaco-editor/react";
import {Button} from "primereact/button";
import {ProductTemplateService} from "api/ProdTemplateApi";
import {PRODUCT_TEMPLATE_AUTOCOMPLETE_MODEL} from "./autocomplete-model";
import {easasAlert, easasErrorAlert} from "../easas-global-fragment/EasasGlobalAlerts";


type TemplateTable = {
    id: bigint,
    refName: string,
    title: string,
}

type ConfiguratorProps = {
    templates: Array<TemplateTable>
}

type ConfiguratorState = {
    selectedTemplate: TemplateTable | null,
    templateConfigJson: string | undefined,
    template: any,
}

loader.init().then((monaco) => {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions(
        PRODUCT_TEMPLATE_AUTOCOMPLETE_MODEL
    )}).catch((error) =>
        console.error("An error occurred during initialization of Monaco: ", error)
    );

class ProductTemplatesConfiguratorJson extends React.Component<ConfiguratorProps, ConfiguratorState> {
    private readonly templateApi = ProductTemplateService()

    constructor(props: ConfiguratorProps) {
        super(props);

        this.state = {
            selectedTemplate: null,
            templateConfigJson: undefined,
            template: {
                displayName: "New Template Display Name",
                safeName: "company_some_table_name",
                keyColumn: "key_column_safe_name",
            },
        }
    }



    handleClear = () => {
        this.setState( {selectedTemplate: null, template: { }, templateConfigJson: undefined})
        easasAlert("Cleared")
    }

    onTemplateChange = (e: DropdownChangeParams) => { this.setState({selectedTemplate: e.target.value}, this.reloadConfig)}
    onDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => { this.setState(state => {state.template.displayName = e.target.value}, this.forceUpdate)}
    onRefNameChange = (e: React.ChangeEvent<HTMLInputElement>) => { this.setState(state => {state.template.safeName = e.target.value},  this.forceUpdate)}
    onKeyAttrChange = (e: React.ChangeEvent<HTMLInputElement>) => { this.setState(state => {state.template.keyColumn = e.target.value}, this.forceUpdate)}
    onMetaKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => { this.setState(state => {state.template.metaKeyColumn = e.target.value},  this.forceUpdate)}

    reloadConfig = () => {
        this.templateApi.getConfig(this.state.selectedTemplate?.refName)
            .then(t => this.setState({
                template: t,
                templateConfigJson: JSON.stringify(t.data, null, 2)
            }));
    }


    updateConfig = () => {
        let ref = this.state.selectedTemplate?.refName

        let err = this.checkJsonAndMergeConfig()
        if(err) return;

        this.templateApi.updateConfig(ref, this.state.template)
            .then(r => {
                if(r.status == undefined){
                    easasAlert(`Template "${r.displayName}" updated`)
                }else{
                    easasErrorAlert(r.message, r.data)
                }
            })
    }
    
    checkJsonAndMergeConfig = () => {
        try{
            this.state.template.data = JSON.parse(this.state.templateConfigJson!!)
        }catch (ex:any){
            easasErrorAlert("JSON Error: " +  ex.message)
            return ex
        }
    }

    createTemplate = () => {
        let err = this.checkJsonAndMergeConfig()
        if(err) return;

        this.templateApi.create(this.state.template)
            .then(r => {
                if(r.status == undefined){
                    easasAlert(`Template "${r.displayName}" created`)
                }else{
                    easasErrorAlert(r.message)
                }
            })

        easasAlert("Template created")
    }

    downloadProdTemplate = () =>  {
            let err = this.checkJsonAndMergeConfig()
            if(err) return;

            let template = this.state.template;
            let fileName = this.state.selectedTemplate?.refName || "no_name_prod_template" + "__" + new Date().toISOString();
            let json = JSON.stringify(template, null, 2);
            let blob = new Blob([json],{type:'application/json'});
            let href = URL.createObjectURL(blob);
            let link = document.createElement('a');
            link.href = href;
            link.download = fileName + ".json";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
    }


    render(){
        const isTableSelected = this.state.selectedTemplate !== null
        let submitButton;

        if(isTableSelected){
            submitButton = <Button label="Save" icon="pi pi-check" onClick={this.updateConfig} iconPos="right" className="p-m-2" style={{width: "50%"}}/>
        }else {
            submitButton = <Button label="Create" icon="pi pi-plus" onClick={this.createTemplate} iconPos="right" className="p-button-success p-m-2" style={{width: "50%"}}/>
        }

        return (
            <div style={{width: "100%", padding: "0 1rem 0 1rem"}}>


                <div className="p-fluid p-formgrid p-grid">
                    {/* ===== Table selection ======= */}
                    <div className="p-field p-col-11">
                        <label htmlFor="type">Template</label>
                        <Dropdown value={this.state.selectedTemplate}
                                  options={this.props.templates}
                                  filter showClear filterBy="title"
                                  onChange={this.onTemplateChange}
                                  optionLabel="title"
                                  placeholder="Select a template"
                        />
                    </div>
                    {/* ===== Table selection ======= */}
                    <div className="p-field p-col-1">
                        <label>Download</label>
                        <Button icon="pi pi-download" style={{width:"100%"}} onClickCapture={this.downloadProdTemplate}/>
                    </div>
                    <div className="p-field p-col-12">
                        <label>Display name</label>
                        <InputText placeholder="Display name" value={this.state.template?.displayName} onChange={this.onDisplayNameChange} className="p-inputtext-sm"/>
                    </div>
                    <div className="p-field p-col-4">
                        <label>Id</label>
                        <InputText placeholder="Not initialized" value={this.state.template?.id} className="p-inputtext-sm" disabled={true}/>
                    </div>
                    <div className="p-field p-col-4">
                        <label>Date created</label>
                        <InputText placeholder="Date created" value={this.state.template?.dateCreated} className="p-inputtext-sm" disabled={true}/>
                    </div>
                    <div className="p-field p-col-4">
                        <label>Date modified</label>
                        <InputText placeholder="Date modified" value={this.state.template?.dateModified} className="p-inputtext-sm" disabled={true}/>
                    </div>
                    <div className="p-field p-col-12">
                        <label>Reference name</label>
                        <InputText placeholder="Reference name (is auto prefixed with company)" value={this.state.template?.safeName} onChange={this.onRefNameChange} className="p-inputtext-sm" readOnly={isTableSelected}/>
                    </div>
                    <div className="p-field p-col-6">
                        <label>Key attribute</label>
                        <InputText placeholder="Key attribute" value={this.state.template?.keyColumn} onChange={this.onKeyAttrChange} className="p-inputtext-sm" disabled={false}/>
                    </div>
                    <div className="p-field p-col-6">
                        <label>Secondary key <i>(meta)</i> attribute</label>
                        <InputText placeholder="Secondary key" value={this.state.template?.metaKeyColumn} onChange={this.onMetaKeyChange} className="p-inputtext-sm" disabled={false}/>
                    </div>
                </div>

                <div className="p-d-flex p-jc-center">
                    <div style={{width: "100%"}}>
                        <Editor
                            height="400px"
                            defaultLanguage="json"
                            value={this.state.templateConfigJson}
                            onChange={(e) => this.setState({templateConfigJson: e})}
                            theme="vs-dark"
                        />
                    </div>
                </div>

                <div className="p-d-flex p-jc-between">
                    {submitButton}
                    <Button label="Clear" icon="pi pi-trash" onClick={this.handleClear} iconPos="right" className="p-button-danger p-m-2" style={{width: "50%"}}/>
                </div>
            </div>
        );
    }
}


export default ProductTemplatesConfiguratorJson;


